import React, { useState, useEffect } from 'react'
import Loading from '../../components/Loading'
import { useParams } from 'react-router-dom'
import { connect } from 'react-redux'
import axios from 'axios'
import { setTitle } from '../../actions'
import { env } from '../../config/env'
import Card from '../../components/Card'
import Table from '../../components/Table'
import styled from 'styled-components'
import formatNumbers from '../../utils/formatNumbers'

const toNumber = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const formatCurrency = value => `$${formatNumbers(toNumber(value), 2)}`

const formatDateTime = value => {
  if (!value) return 'N/A'

  const parsedDate = new Date(value)
  if (Number.isNaN(parsedDate.getTime())) return 'N/A'

  return parsedDate.toLocaleString()
}

const displayValue = value => {
  if (value === null || value === undefined) return 'N/A'
  const text = String(value).trim()
  return text === '' ? 'N/A' : text
}

const composeAddress = data => {
  if (!data || typeof data !== 'object') return 'N/A'

  const parts = [
    data.address,
    data.street,
    data.city,
    data.state,
    data.postalCode,
    data.zipCode,
  ]
    .map(value => (value === null || value === undefined ? '' : String(value).trim()))
    .filter(Boolean)

  return parts.length ? parts.join(', ') : 'N/A'
}

const OrderDetail = props => {
  const { setTitle } = props
  const { id } = useParams()
  const [order, setOrder] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    setTitle({ title: `Detalle de Orden #${id}` })
  }, [id, setTitle])

  useEffect(() => {
    const fetchOrderDetail = async () => {
      const storedJwt = sessionStorage.getItem('token')

      try {
        const res = await axios({
          url: `${env.REACT_APP_API}orders/detail/${id}`,
          headers: { Authorization: `Bearer ${storedJwt}` },
          method: 'get',
        })

        setOrder(res?.data?.data || null)
        setError('')
      } catch (err) {
        setOrder(null)
        setError('No se pudo cargar el detalle del pedido')
      }
    }

    fetchOrderDetail()
  }, [id])

  if (error) return <h2>{error}</h2>

  if (!order) return <Loading />

  const payment = order.payment || {}
  const shipping = order.shipping || {}
  const customer = payment.customerInfo || {}
  const recipient = shipping.recipient || {}
  const shipper = shipping.shipper || {}
  const items = Array.isArray(order.items) ? order.items : []
  const sapDocuments = Array.isArray(order.sapDocuments) ? order.sapDocuments : []

  const subtotalWithoutIVA = toNumber(order.subtotalWithoutIVA)
  const ivaAmount = toNumber(order.ivaAmount)
  const orderTotal =
    toNumber(order.total) ||
    toNumber(payment.amount)

  const productColumns = [
    {
      accessorKey: 'itemName',
      header: 'Producto',
    },
    {
      accessorKey: 'brandName',
      header: 'Marca',
      Cell: ({ row }) => displayValue(row.original.brandName),
    },
    {
      accessorKey: 'color',
      header: 'Color',
    },
    {
      accessorKey: 'size',
      header: 'Talla',
    },
    {
      accessorKey: 'quantity',
      header: 'Cantidad',
    },
    {
      accessorKey: 'imageUrl',
      header: 'Imagen',
      Cell: ({ row }) =>
        row.original.imageUrl ? (
          <img
            src={row.original.imageUrl}
            alt={row.original.itemName}
            width={50}
          />
        ) : (
          'N/A'
        ),
    },
  ]

  const sapDocumentColumns = [
    {
      accessorKey: 'type',
      header: 'Documento',
      Cell: ({ row }) =>
        row.original.brandDb
          ? `${row.original.type} (${row.original.brandDb})`
          : row.original.type,
    },
    {
      accessorKey: 'docNum',
      header: 'DocNum',
      Cell: ({ row }) => displayValue(row.original.docNum),
    },
    {
      accessorKey: 'docEntry',
      header: 'DocEntry',
      Cell: ({ row }) => displayValue(row.original.docEntry),
    },
    {
      accessorKey: 'companyDb',
      header: 'Base de datos',
      Cell: ({ row }) => displayValue(row.original.companyDb),
    },
    {
      accessorKey: 'createdAt',
      header: 'Fecha',
      Cell: ({ row }) => formatDateTime(row.original.createdAt),
    },
  ]

  return (
    <>
      <Card title="Resumen del Pedido">
        <InfoGroup>
          <div><strong>Folio:</strong> {order.orderId}</div>
          <div><strong>Pedido:</strong> {order.orderNumber || 'N/A'}</div>
          <div><strong>Estatus:</strong> {order.status}</div>
          <div><strong>Fecha de Creación:</strong> {formatDateTime(order.createdAt)}</div>
          <div><strong>Fecha de Pago:</strong> {formatDateTime(payment.paymentDate)}</div>
        </InfoGroup>
        <SummaryGrid>
          <SummaryCard>
            <span>Total sin IVA</span>
            <strong>{formatCurrency(subtotalWithoutIVA)}</strong>
          </SummaryCard>
          <SummaryCard>
            <span>IVA</span>
            <strong>{formatCurrency(ivaAmount)}</strong>
          </SummaryCard>
          <SummaryCard>
            <span>Total de la orden</span>
            <strong>{formatCurrency(orderTotal)}</strong>
          </SummaryCard>
        </SummaryGrid>
      </Card>
      <Card title="Documentos SAP">
        {sapDocuments.length ? (
          <Table data={sapDocuments} columns={sapDocumentColumns} name="Documentos SAP" />
        ) : (
          <InfoGroup>
            <div>Aún no se han generado documentos en SAP para este pedido.</div>
          </InfoGroup>
        )}
      </Card>
      {(order.lastErrorStep || order.lastErrorMessage || order.last_error_step || order.last_error_message) ? (
        <Card title="Último error (integración)">
          <InfoGroup>
            <div><strong>Paso:</strong> {displayValue(order.lastErrorStep || order.last_error_step)}</div>
            <div><strong>Mensaje:</strong> {displayValue(order.lastErrorMessage || order.last_error_message)}</div>
            <div><strong>Fecha:</strong> {formatDateTime(order.lastErrorAt || order.last_error_at)}</div>
          </InfoGroup>
        </Card>
      ) : null}


      <Card title="Envío">
        <InfoGroup>
          <div><strong>Proveedor:</strong> {displayValue(shipping.provider)}</div>
          <div><strong>Servicio:</strong> {displayValue(shipping.serviceName)}</div>
          <div><strong>Costo:</strong> {formatCurrency(shipping.cost)} {displayValue(shipping.currency)}</div>
          <div><strong>Guía:</strong> {displayValue(order.fedexGuide)}</div>
          <div><strong>Entrega Estimada:</strong> {displayValue(shipping.estimatedDelivery)}</div>
        </InfoGroup>
        <InfoGroup>
          <div><strong>Destinatario:</strong> {displayValue(recipient.name)}</div>
          <div><strong>Teléfono:</strong> {displayValue(recipient.phone)}</div>
          <div><strong>Dirección:</strong> {composeAddress(recipient)}</div>
        </InfoGroup>
        <InfoGroup>
          <div><strong>Remitente:</strong> {displayValue(shipper.name)} ({displayValue(shipper.company)})</div>
          <div><strong>Teléfono:</strong> {displayValue(shipper.phone)}</div>
          <div><strong>Dirección:</strong> {composeAddress(shipper)}</div>
        </InfoGroup>
      </Card>

      <Card title="Productos">
        <Table data={items} columns={productColumns} name="Productos" />
      </Card>
    </>
  )
}

const InfoGroup = styled.div`
  padding: 10px 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: 10px;
  column-gap: 20px;
  font-size: 15px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const SummaryGrid = styled.div`
  padding-top: 10px;
  display: grid;
  grid-template-columns: repeat(3, minmax(160px, 1fr));
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(160px, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`

const SummaryCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  background: #fafafa;

  span {
    display: block;
    font-size: 12px;
    color: #6b7280;
    margin-bottom: 4px;
  }

  strong {
    font-size: 16px;
  }
`

const mapDispatchToProps = {
  setTitle,
}

export default connect(null, mapDispatchToProps)(OrderDetail)
